import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { ObtenerCompanies } from "../../../Service/Service.compania";
import { ListarClinicas } from "../../../Service/Serive.clinica";
import {
  FinalizarContextoSoporte,
  IniciarContextoSoporte,
  ListarAuditEvents,
  ListarHistorialAccesos,
  ListarSesionesSoporte,
} from "../../../Service/Service.security";

const emptyForm = { company_id: "", clinic_id: "", reason: "" };

export const formatAuditDate = (value) => {
  if (!value) return "";
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    const mysqlLike = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
    date = mysqlLike
      ? new Date(
          Number(mysqlLike[1]),
          Number(mysqlLike[2]) - 1,
          Number(mysqlLike[3]),
          Number(mysqlLike[4]),
          Number(mysqlLike[5]),
          Number(mysqlLike[6])
        )
      : new Date(value);
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const compactChanges = (changes) => {
  if (!changes) return "";
  if (Array.isArray(changes.added) || Array.isArray(changes.removed)) {
    return `+${changes.added?.length || 0} / -${changes.removed?.length || 0}`;
  }
  if (Array.isArray(changes.fields_changed)) {
    return changes.fields_changed.join(", ");
  }
  return Object.keys(changes).slice(0, 6).join(", ");
};

export const getAccessRows = (response) => (Array.isArray(response?.data?.rows) ? response.data.rows : []);
export const getSessionRows = (response) => (Array.isArray(response?.data?.rows) ? response.data.rows : []);
export const getAuditRows = (response) => (Array.isArray(response?.data?.items) ? response.data.items : []);

export default function SecurityAccessIndex() {
  const [companies, setCompanies] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [accessRows, setAccessRows] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [accessPage, setAccessPage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);
  const [accessTotal, setAccessTotal] = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const filteredClinics = useMemo(() => {
    if (!form.company_id) return [];
    return clinics.filter((clinic) => Number(clinic.idcompany) === Number(form.company_id));
  }, [clinics, form.company_id]);

  const activeSession = useMemo(
    () => sessions.find((session) => session.active),
    [sessions]
  );

  const loadCatalogs = useCallback(async () => {
    const [companiesResponse, clinicsResponse] = await Promise.all([
      ObtenerCompanies(),
      ListarClinicas(),
    ]);
    setCompanies(companiesResponse || []);
    setClinics(clinicsResponse || []);
  }, []);

  const loadSecurityData = useCallback(async (page = accessPage) => {
    setLoadError("");
    setLoading(true);
    try {
      const [accessResponse, sessionsResponse, auditResponse] = await Promise.all([
        ListarHistorialAccesos({ page, page_size: 25 }),
        ListarSesionesSoporte({ page: 1, page_size: 25 }),
        ListarAuditEvents({ page: auditPage, page_size: 25 }),
      ]);
      setAccessRows(getAccessRows(accessResponse));
      setSessions(getSessionRows(sessionsResponse));
      setAuditRows(getAuditRows(auditResponse));
      setAccessTotal(Number(accessResponse?.data?.total) || 0);
      setAuditTotalPages(Math.max(1, Number(auditResponse?.data?.pagination?.total_pages) || 1));
    } catch (error) {
      setAccessRows([]);
      setSessions([]);
      setAuditRows([]);
      setLoadError("No se pudo cargar la información de seguridad.");
    } finally {
      setLoading(false);
    }
  }, [accessPage, auditPage]);

  useEffect(() => {
    loadCatalogs();
    loadSecurityData(1);
  }, [loadCatalogs, loadSecurityData]);

  const startSupport = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await IniciarContextoSoporte({
        company_id: Number(form.company_id),
        clinic_id: form.clinic_id ? Number(form.clinic_id) : undefined,
        reason: form.reason,
      });
      if (response?.success) {
        setForm(emptyForm);
        await loadSecurityData(1);
        Swal.fire("Listo", response.message, "success");
      } else {
        Swal.fire("Atención", response?.message || "No se pudo iniciar soporte", "warning");
      }
    } catch (error) {
      Swal.fire("Error", "No se pudo iniciar soporte", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeSupport = async (id) => {
    setLoading(true);
    try {
      const response = await FinalizarContextoSoporte(id);
      if (response?.success) {
        await loadSecurityData(1);
        Swal.fire("Listo", response.message, "success");
      } else {
        Swal.fire("Atención", response?.message || "No se pudo finalizar soporte", "warning");
      }
    } catch (error) {
      Swal.fire("Error", "No se pudo finalizar soporte", "error");
    } finally {
      setLoading(false);
    }
  };

  const nextPage = async () => {
    const next = accessPage + 1;
    setAccessPage(next);
    await loadSecurityData(next);
  };

  const nextAuditPage = async () => {
    const next = auditPage + 1;
    setAuditPage(next);
    const response = await ListarAuditEvents({ page: next, page_size: 25 });
    setAuditRows(getAuditRows(response));
    setAuditTotalPages(Math.max(1, Number(response?.data?.pagination?.total_pages) || 1));
  };

  const prevAuditPage = async () => {
    const prev = Math.max(1, auditPage - 1);
    setAuditPage(prev);
    const response = await ListarAuditEvents({ page: prev, page_size: 25 });
    setAuditRows(getAuditRows(response));
    setAuditTotalPages(Math.max(1, Number(response?.data?.pagination?.total_pages) || 1));
  };

  const prevPage = async () => {
    const prev = Math.max(1, accessPage - 1);
    setAccessPage(prev);
    await loadSecurityData(prev);
  };

  const accessCanNext = accessPage * 25 < accessTotal;
  const auditCanNext = auditPage < auditTotalPages;

  return (
    <div>
      {loadError && <div className="alert alert-danger py-2">{loadError}</div>}
      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="border rounded p-3 h-100">
            <h5 className="font-size-15 mb-3">Contexto de soporte</h5>
            {activeSession && (
              <div className="alert alert-info py-2">
                Sesión activa #{activeSession.id} · Company {activeSession.company_id}
                {activeSession.clinic_id ? ` · Clínica ${activeSession.clinic_id}` : ""}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary float-end"
                  disabled={loading}
                  onClick={() => closeSupport(activeSession.id)}
                >
                  Finalizar
                </button>
              </div>
            )}
            <form onSubmit={startSupport}>
              <div className="mb-3">
                <label className="form-label">Company</label>
                <select
                  className="form-select"
                  value={form.company_id}
                  disabled={Boolean(activeSession)}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value, clinic_id: "" })}
                  required
                >
                  <option value="">Seleccione</option>
                  {companies.map((company) => (
                    <option key={company.idcompany} value={company.idcompany}>
                      {company.company}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Clínica</label>
                <select
                  className="form-select"
                  value={form.clinic_id}
                  disabled={Boolean(activeSession) || !form.company_id}
                  onChange={(e) => setForm({ ...form, clinic_id: e.target.value })}
                >
                  <option value="">Todas / opcional</option>
                  {filteredClinics.map((clinic) => (
                    <option key={clinic.idclinica} value={clinic.idclinica}>
                      {clinic.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Motivo</label>
                <textarea
                  className="form-control"
                  rows="3"
                  maxLength="255"
                  value={form.reason}
                  disabled={Boolean(activeSession)}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  required
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={loading || Boolean(activeSession)}>
                Iniciar soporte
              </button>
            </form>
          </div>
        </div>
        <div className="col-lg-7">
          <div className="border rounded p-3 h-100">
            <h5 className="font-size-15 mb-3">Sesiones de soporte</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Master</th>
                    <th>Company</th>
                    <th>Clínica</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && sessions.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-muted text-center">Cargando...</td>
                    </tr>
                  )}
                  {!loading && sessions.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-muted text-center">Sin sesiones de soporte.</td>
                    </tr>
                  )}
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td>{session.id}</td>
                      <td>{session.master_user_id}</td>
                      <td>{session.company_id}</td>
                      <td>{session.clinic_id || ""}</td>
                      <td>{formatAuditDate(session.started_at)}</td>
                      <td>{formatAuditDate(session.ended_at)}</td>
                      <td>{session.active ? "Activa" : "Cerrada"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="border rounded p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="font-size-15 mb-0">Historial de accesos</h5>
          <div className="btn-group">
            <button className="btn btn-outline-secondary btn-sm" type="button" disabled={accessPage === 1} onClick={prevPage}>
              Anterior
            </button>
            <button className="btn btn-outline-secondary btn-sm" type="button" disabled={!accessCanNext} onClick={nextPage}>
              Siguiente
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Usuario ID</th>
                <th>Evento</th>
                <th>Resultado</th>
                <th>IP</th>
                <th>Agente</th>
                <th>Contexto soporte</th>
              </tr>
            </thead>
            <tbody>
              {loading && accessRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-muted text-center">Cargando...</td>
                </tr>
              )}
              {!loading && accessRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-muted text-center">Sin eventos de acceso.</td>
                </tr>
              )}
              {accessRows.map((row) => (
                <tr key={row.id}>
                  <td>{formatAuditDate(row.created_at)}</td>
                  <td>{row.user_id || ""}</td>
                  <td>{row.event}</td>
                  <td>{row.success ? "OK" : "Fallo"}</td>
                  <td>{row.ip}</td>
                  <td className="text-truncate" style={{ maxWidth: 260 }}>{row.user_agent || ""}</td>
                  <td>{row.support_session_id || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border rounded p-3 mt-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="font-size-15 mb-0">Auditoría de cambios</h5>
          <div className="btn-group">
            <button className="btn btn-outline-secondary btn-sm" type="button" disabled={auditPage === 1} onClick={prevAuditPage}>
              Anterior
            </button>
            <button className="btn btn-outline-secondary btn-sm" type="button" disabled={!auditCanNext} onClick={nextAuditPage}>
              Siguiente
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Actor ID</th>
                <th>Acción</th>
                <th>Entidad</th>
                <th>ID</th>
                <th>Company</th>
                <th>Clínica</th>
                <th>Contexto soporte</th>
                <th>Cambios</th>
              </tr>
            </thead>
            <tbody>
              {loading && auditRows.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-muted text-center">Cargando...</td>
                </tr>
              )}
              {!loading && auditRows.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-muted text-center">Sin eventos de auditoría.</td>
                </tr>
              )}
              {auditRows.map((row) => (
                <tr key={row.id}>
                  <td>{formatAuditDate(row.created_at)}</td>
                  <td>{row.actor_user_id}</td>
                  <td>{row.action}</td>
                  <td>{row.entity_type}</td>
                  <td>{row.entity_id || ""}</td>
                  <td>{row.company_id || ""}</td>
                  <td>{row.clinic_id || ""}</td>
                  <td>{row.support_session_id || ""}</td>
                  <td className="text-truncate" style={{ maxWidth: 260 }}>{compactChanges(row.changes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
